import { ElementRef, Injectable, QueryList } from '@angular/core';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';
import { IBulkItemId } from '@nimbus-admin/infrastructure/bulk-process/contracts/i-bulk-item-id';
import { BulkActionsOverlayHelper } from '@nimbus-global/features/bulk-edit/helpers/bulk-actions-overlay.helper';

@Injectable()
export class BulkProcessService<TId> {
  private bulkItems$: BehaviorSubject<IBulkItemId<TId>[]> = new BehaviorSubject([]);
  private bulkItemsSelectedIds$ = new BehaviorSubject<TId[]>([]);

  public bulkOverlayOrigin$ = new BehaviorSubject<ElementRef>(null);
  public isBulkOverlayOpen$ = new BehaviorSubject(false);
  public areAllBulkItemsSelected$ = new BehaviorSubject(false);

  public initialize(getBulkItemsAllCheckBox: () => ElementRef, getBulkItemCheckBoxes: () => QueryList<ElementRef>): void {
    this.getBulkItemsAllCheckBox = getBulkItemsAllCheckBox;
    this.getBulkItemCheckBoxes = getBulkItemCheckBoxes;
  }

  public initializeBulkItems(items: IBulkItemId<TId>[]): void {
    const bulkItemsSelectedIdsOld = this.bulkItemsSelectedIds$.value;
    const bulkItemsSelectedIdsNew = bulkItemsSelectedIdsOld.filter((id) => items.some((item) => isEqual(id, item.bulkItemId)));
    this.clearBulkProcess();
    this.bulkItems$.next(items);
    this.updateBulkItemsSelectedIds$(bulkItemsSelectedIdsNew);
    if (this.hasBulkItemsSelected) {
      this.openBulkOverlay();
    }
  }

  public getBulkItems$(): Observable<IBulkItemId<TId>[]> {
    return this.bulkItems$.asObservable();
  }

  public getBulkItemsSelectedIds$(): Observable<TId[]> {
    return this.bulkItemsSelectedIds$.asObservable();
  }

  public getBulkItemsSelectedIds(): TId[] {
    return this.bulkItemsSelectedIds$.value;
  }

  public isBulkItemSelected(dataItem: IBulkItemId<TId>): boolean {
    const bulkItemsSelectedIds = this.bulkItemsSelectedIds$.value;
    return bulkItemsSelectedIds.some((id) => isEqual(id, dataItem.bulkItemId));
  }

  public changeAllBulkItemsSelection(isChecked: boolean): void {
    this.areAllBulkItemsSelected$.next(isChecked);
    let selectedItems = [];
    if (isChecked) {
      selectedItems = this.bulkItems$.value.map((x) => x.bulkItemId);
    }
    this.updateBulkItemsSelectedIds$(selectedItems);
    if (isChecked && this.hasBulkItemsSelected) {
      this.openBulkOverlay();
    }
  }

  public changedBulkItemSelection(isChecked: boolean, item: IBulkItemId<TId>): void {
    const bulkItemsSelectedIds = this.bulkItemsSelectedIds$.value;
    if (isChecked) {
      bulkItemsSelectedIds.push(item.bulkItemId);
    } else {
      this.areAllBulkItemsSelected$.next(false);
      const itemIndex = bulkItemsSelectedIds.findIndex((id) => isEqual(id, item.bulkItemId));
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      if (itemIndex > -1) {
        bulkItemsSelectedIds.splice(itemIndex, 1);
      }
    }
    this.updateBulkItemsSelectedIds$(bulkItemsSelectedIds);
    if (bulkItemsSelectedIds.length > 0) {
      this.openBulkOverlay();
    } else {
      this.closeBulkOverlay();
    }
  }

  public closeBulkOverlay(): void {
    this.isBulkOverlayOpen$.next(false);
  }

  public clearBulkProcess(): void {
    this.updateBulkItemsSelectedIds$([]);
    this.closeBulkOverlay();
  }

  private get hasBulkItemsSelected(): boolean {
    return this.bulkItemsSelectedIds$.value?.length > 0;
  }

  private getBulkItemsAllCheckBox: () => ElementRef = () => undefined;
  private getBulkItemCheckBoxes: () => QueryList<ElementRef> = () => undefined;

  private openBulkOverlay(): void {
    setTimeout(() => {
      const rowIds = this.bulkItems$.value.map((item) => item.bulkItemId);
      this.bulkOverlayOrigin$.next(
        BulkActionsOverlayHelper.getOrigin(
          rowIds,
          this.bulkItemsSelectedIds$.value,
          this.getBulkItemCheckBoxes(),
          this.getBulkItemsAllCheckBox(),
        ),
      );
      this.isBulkOverlayOpen$.next(true);
    });
  }

  private updateBulkItemsSelectedIds$(bulkItemsSelectedIds: TId[]): void {
    this.bulkItemsSelectedIds$.next(bulkItemsSelectedIds);
    if (bulkItemsSelectedIds.length === this.bulkItems$.value.length) {
      this.areAllBulkItemsSelected$.next(true);
    }
    if (bulkItemsSelectedIds.length === 0) {
      this.areAllBulkItemsSelected$.next(false);
      this.closeBulkOverlay();
    }
  }
}
